import * as actions from "../actions";
import { store } from "..";
import { sendParentFilter } from "../parentCommunication";
import _ from "lodash";
import { AppMode } from "../constants";

export interface Product {
	id: number;
	nr: string;
	gutterShape: string;
	grille: string;
	tubeShape: string;
	material: string;
	size: string;
	symKar: string;
	file: string;
	listFile: string;
	color: string;
	colorName: string;
	fontColor: string;
	selectionColor: string;
	selectionBrighten: number;
	bgColor: string;
	namePL: string;
	nameEN: string;
	nameRU: string;
	nameDE: string;
	posX: number;
	posY: number;
	posZ: number;
	units: Unit[];
}

export interface Unit {
    unit: string;
    isDefault: boolean;
    converter: number;
    description: string;
}

export interface Assortment {
	products: Product[];
	gutterShapes: string[];
	materials: string[];
	grilles: string[];
	tubeShapes: string[];
	sizes: string[];
	colors: {[key: string]: string}
}

export interface Filters {
	gutterShape: string | null;
	material: string | null;
	grille: string | null;
	tubeShape: string | null;
	size: string | null;
	colorName: string | null;
}

export const DEFAULT_FILTERS = {
	gutterShape: null,
	material: null,
	grille: null,
	tubeShape: null,
	size: null,
	colorName: null
}

export const matchesFilters = (obj1: Product | Filters, obj2: Filters): boolean => {
	let matches: boolean = true;
	
	Object.keys(obj2).forEach(key => {
		if (obj2[key] !== obj1[key]) matches = false;
	});

	return matches;
}

export const clearFilters = () => {
	store.dispatch({
		type: actions.CLEAR_FILTERS,
		payload: {}
	});
	
	filterAssortment();

	if (store.getState().config.appMode === AppMode.Order){
		_.forOwn(DEFAULT_FILTERS, (value, key) => {
			sendParentFilter({filterName: key, value: undefined});
		})
	}	
}

export const setFilter = (name: string, value: string) => {
	store.dispatch({
		type: actions.SET_FILTER,
		payload: {
			name,
			value
		}
	});

	filterAssortment();

	if (store.getState().config.appMode === AppMode.Order){
		sendParentFilter({filterName: name, value: value});
	}
	
}

export const getPossibleFilters = () => {
	const state = store.getState();

	return {
		gutterShapes: state.filteredAssortment.gutterShapes,
		materials: state.filteredAssortment.materials,
		grilles: state.filteredAssortment.grilles,
		tubeShapes: state.filteredAssortment.tubeShapes,
		sizes: state.filteredAssortment.sizes,
		colors: state.filteredAssortment.colors
	}
}

const filterProductsByFilters = (products: Product[], filters: Filters): {products: Product[], filters: {[key: string]: string[]}} => {
	const actualFilters = {
		gutterShapes: [],
		materials: [],
		grilles: [],
		tubeShapes: [],
		sizes: [],
		colorNames: [],
	};

	const filteredProducts = products.filter(prd => {
		let ret = true;
		if (filters['gutterShape']) ret = ret && (prd.gutterShape === filters['gutterShape']);
		if (filters['material']) ret = ret && (prd.material === filters['material']);
		if (filters['grille']) ret = ret && (prd.grille === filters['grille']);
		if (filters['tubeShape']) ret = ret && (prd.tubeShape === filters['tubeShape']);
		if (filters['size']) ret = ret && (prd.size === filters['size']);
		if (filters['colorName']) ret = ret && (prd.colorName === filters['colorName']);
		if (ret) {
			if (actualFilters.gutterShapes.indexOf(prd.gutterShape) === -1) actualFilters.gutterShapes.push(prd.gutterShape);
			if (actualFilters.materials.indexOf(prd.material) === -1) actualFilters.materials.push(prd.material);
			if (actualFilters.grilles.indexOf(prd.grille) === -1) actualFilters.grilles.push(prd.grille);
			if (actualFilters.tubeShapes.indexOf(prd.tubeShape) === -1) actualFilters.tubeShapes.push(prd.tubeShape);
			if (actualFilters.sizes.indexOf(prd.size) === -1) actualFilters.sizes.push(prd.size);
			if (actualFilters.colorNames.indexOf(prd.colorName) === -1) actualFilters.colorNames.push(prd.colorName);
		}
		return ret;
	});

	return {
		products: filteredProducts,
		filters: actualFilters
	}
}

const filterAssortment = () => {
	const state = store.getState();

	store.dispatch({
		type: actions.SET_IS_FILTERING,
		payload: true
	});

	const assortment: Assortment = state.assortment;
	let filters = state.filters;

	const actualFilters = {
		gutterShapes: [],
		materials: [],
		grilles: [],
		tubeShapes: [],
		sizes: [],
		colorNames: [],
	};

	Object.keys(filters).map(filter => {
		const tempFilters = {...filters};
		tempFilters[filter] = null;
		actualFilters[filter + 's'] = filterProductsByFilters(assortment.products, tempFilters).filters[filter + 's'];
	});

	const productsToShow = filterProductsByFilters(assortment.products, filters).products;

	let filteredAssortment: Assortment = {
		products: productsToShow,
		gutterShapes: actualFilters.gutterShapes,
		materials: actualFilters.materials,
		grilles: actualFilters.grilles,
		tubeShapes: actualFilters.tubeShapes,
		sizes: actualFilters.sizes,
		colors: Object.fromEntries(Object.entries(assortment.colors).filter(([key]) => actualFilters.colorNames.includes(key))),
	}

	store.dispatch({
		type: actions.SET_FILTERED_ASSORTMENT,
		payload: filteredAssortment
	})

	store.dispatch({
		type: actions.SET_IS_FILTERING,
		payload: false
	});
}

export const buildProductImagesLinks = (prd: Product) => {
	const { config: { articlesImgPath } } = store.getState();

	return {
		canvasPath: `${articlesImgPath}${prd.file}`,
		listPath: `${articlesImgPath}${prd.listFile}`
	}
}

export const getSelectedImagePath = () => {
	const { selected, assortment: { products } } = store.getState();
	
	if (selected  === null || selected === -1) return '';
	return buildProductImagesLinks(products[selected]).canvasPath;
}

export const getSelectedProduct = (): Product | null => {
	const { selected, assortment: { products } } = store.getState();

	return products.find(prd => prd.id === selected);
}
